<template>
  <el-dialog v-model="visible" class="small no-header no-padding" @close="visible = false" :append-to-body="true">
    <div v-loading="loading" element-loading-background="transparent" class="check-container">
      <div class="code-info">
        <p>{{ $t('Please enter the verification code sent to') }}</p>
        <span>{{ account }}</span>
      </div>
      <div>
        <CodeInput ref="refCodeInput" v-if="visible" class="code-input" @on-ok="handleCheckAccount" />
        <div class="countdown">
          <p class="countdown-time" v-if="countDownLastTime > 0">{{ countDownLastTime }} {{ $t('seconds remaining to resend code') }}</p>
          <span class="resend" v-else @click="handleSendCode(true)">{{ $t('Resend') }}</span>
        </div>
        <template v-if="props.canChangeAccountType && userPhone && userEmail">
          <el-button v-if="accountType === 'phone' && userEmail" type="text" class="other-account-type" @click="handleChangeAuthType('email', userEmail)">{{ $t('Use email verification') }}</el-button>
          <el-button v-else-if="accountType === 'email' && userPhone" type="text" class="other-account-type" @click="handleChangeAuthType('phone', userPhone)">{{ $t('Use phone verification') }}</el-button>
        </template>
        <slot />
      </div>
    </div>
  </el-dialog>
</template>

<script setup>
import { verifyCaptcha } from '@/api/user/user';
import CodeInput from '@/components/code-input.vue';
import enums from '@/libs/enums';
import { isAppleEmail } from '@/libs/tools';
import { getSessionStorage, setSessionStorage } from '@/libs/utils';
import { sendCaptcha } from '@/services/login';
import { appStore, userStore } from '@/stores';
import { computed, nextTick, ref } from 'vue';

const props = defineProps({
  // 验证类型：bind/auth/resetPass
  authType: {
    type: String,
    default: 'bind'
  },
  // 是否可切换账号类型：如果为true且邮箱和手机号都有绑定的情况下可切换
  canChangeAccountType: {
    type: Boolean,
    default: false
  }
})

// 验证码类型
const sendCodeType = computed(() => {
  return {
    'bind': enums.sendCaptchaType.login,
    'auth': enums.sendCaptchaType.login,
    'resetPass': enums.sendCaptchaType.resetPass
  }[props.authType]
})

const emits = defineEmits(['on-success', 'on-change-type'])

const userPhone = computed(() => userStore.userInfo?.phone)
const userEmail = computed(() => !isAppleEmail(userStore.userInfo?.email) ? userStore.userInfo?.email : '')

// 账号类型：email / phone
const accountType = ref('email')
// 邮箱或手机号
const account = ref('')
const visible = defineModel(false)
// 发送验证码和验证码验证时loading状态
const loading = ref(false)

/**
 * 打开验证弹窗
 * @param type email / phone
 * @param checkAccount 邮箱或手机号
 */
const open = (type, checkAccount) => {
  accountType.value = type
  account.value = checkAccount
  visible.value = true
  handleSendCode()
}

/**
 * 切换验证方式
 * @param type 
 * @param checkAccount 
 */
const handleChangeAuthType = (type, checkAccount) => {
  emits('on-change-type', type, checkAccount)
  open(type, checkAccount)
}

// 发送验证码
const countDownLastTime = ref(0)
const refCodeInput = ref(null)
const countDownAllLastTime = appStore.isProd ? 60 : 20
let codeId = '', countDownTimer = null
// 记录缓存，用于刷新页面后再次弹窗时恢复倒计时
const countSendTimeKey = computed(() => `check-account-send-time_${sendCodeType.value}_${accountType.value}_${account.value}`)
const codeIdKey = computed(() => `check-account-code-id_${sendCodeType.value}_${accountType.value}_${account.value}`)
// 开始倒计时
const countDownTime = () => {
  if (countDownTimer) {
    clearInterval(countDownTimer)
    countDownTimer = null
  }
  countDownTimer = setInterval(() => {
    countDownLastTime.value--
    if (countDownLastTime.value <= 0) {
      clearInterval(countDownTimer)
      countDownTimer = null
      setSessionStorage(countSendTimeKey.value, '')
      setSessionStorage(codeIdKey.value, '')
    }
  }, 1000)
  nextTick(() => {
    refCodeInput.value.clearCode()
    refCodeInput.value.focus()
  })
}
const handleSendCode = async (isResend = false) => {
  if (loading.value && countDownLastTime.value === 0) return
  const cacheSendTime = parseInt(getSessionStorage(countSendTimeKey.value) || 0)
  const now = new Date().getTime()
  const cacheLastTime = countDownAllLastTime - parseInt((now - cacheSendTime) / 1000)
  const cacheCodeId = getSessionStorage(codeIdKey.value)
  if (cacheCodeId) {
    codeId = cacheCodeId
    // 如果距离上次发送时间小于60秒，则直接返回并开始倒计时
    if (cacheLastTime < countDownAllLastTime) {
      countDownLastTime.value = cacheLastTime
      countDownTime()
      return
    }
  }
  
  loading.value = true
  try {
    const data = await sendCaptcha(account.value, sendCodeType.value, isResend)
    codeId = data.codeId
    countDownLastTime.value = countDownAllLastTime
    setSessionStorage(countSendTimeKey.value, new Date().getTime())
    setSessionStorage(codeIdKey.value, codeId)
    countDownTime()
  } catch (e) {
    console.log(e)
  }
  loading.value = false
}

// 检测验证码
const handleCheckAccount = async code => {
  if (loading.value) return
  loading.value = true
  code = parseInt(code)
  const data = {
    username: account.value,
    type: sendCodeType.value,
    codeId,
    code,
  }
  try {
    await verifyCaptcha(data)
    setSessionStorage(countSendTimeKey.value, '')
    setSessionStorage(codeIdKey.value, '')
    if (countDownTimer) {
      clearInterval(countDownTimer)
      countDownTimer = null
    }
    visible.value = false
    emits('on-success', codeId, code, account.value)
  } catch (e) {
    refCodeInput.value.clearCode()
    refCodeInput.value.focus()
    console.log(e)
  }
  loading.value = false
}

defineExpose({
  open
})
</script>

<style lang="scss" scoped>
.code-info {
  margin-top: 48px;
  line-height: 24px;
  text-align: center;
}
.code-input {
  margin: 56px auto 40px;
  max-width: 296px;
}
.countdown {
  text-align: center;
  .resend {
    cursor: pointer;
    color: var(--Color-1-text-hover, #5C62FF);
    font-size: 14px;
  }
}
.other-account-type {
  display: block;
  margin: 48px auto;
}
</style>